/* eslint-disable react/no-unescaped-entities */
/* Lojistik — Konteyner / BL takip & ShipsGo entegrasyonu. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
    Ship, UploadCloud, Loader2, RefreshCw, Trash2, Plus, X, Search,
    Anchor, MapPin, Calendar, Package as PkgIcon, FileText, AlertCircle,
    Settings as SettingsIcon, ExternalLink, ChevronDown, ChevronRight,
    Download, Save, BookOpen,
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_META = {
    NEW: { tr: 'Yeni', bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
    INPROGRESS: { tr: 'İşleniyor', bg: 'bg-amber-50', text: 'text-amber-800', dot: 'bg-amber-500' },
    BOOKED: { tr: 'Rezerve', bg: 'bg-blue-50', text: 'text-blue-800', dot: 'bg-blue-500' },
    LOADED: { tr: 'Yüklendi', bg: 'bg-indigo-50', text: 'text-indigo-800', dot: 'bg-indigo-500' },
    SAILING: { tr: 'Denizde 🚢', bg: 'bg-cyan-50', text: 'text-cyan-800', dot: 'bg-cyan-500' },
    ARRIVED: { tr: 'Limana ulaştı', bg: 'bg-emerald-50', text: 'text-emerald-800', dot: 'bg-emerald-500' },
    DISCHARGED: { tr: 'Tahliye edildi ✅', bg: 'bg-green-50', text: 'text-green-800', dot: 'bg-green-600' },
    UNTRACKED: { tr: 'Takip edilemez', bg: 'bg-rose-50', text: 'text-rose-800', dot: 'bg-rose-500' },
};

const EVENT_TR = {
    EMSH: 'Boş konteyner teslim alındı',
    GTIN: 'Limana giriş',
    LOAD: 'Gemiye yüklendi',
    DEPA: 'Limandan ayrıldı',
    ARRV: 'Limana vardı',
    DISC: 'Tahliye edildi',
    GTOT: 'Limandan çıkış',
    EMRT: 'Boş dönüş',
};

const fmt = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return d.slice(0, 10); }
};

export default function Logistics() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('list');
    const [cfg, setCfg] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        axios.get(`${API}/api/logistics/config`, { headers: hdrs() })
            .then(r => setCfg(r.data))
            .catch(() => setCfg({}));
    }, []);

    const bump = () => setRefreshKey(k => k + 1);

    return (
        <DashboardLayout>
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-[1400px] mx-auto px-3 md:px-6 py-3 md:py-5">
                    <div className="flex items-center gap-3 mb-4">
                        <Ship size={22} className="text-cyan-700" />
                        <h1 className="text-lg md:text-xl font-bold text-gray-900">Lojistik · Konteyner Takip</h1>
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded-full">ShipsGo</span>
                    </div>

                    {cfg && !cfg.shipsgo_configured && (
                        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                            <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1 text-sm">
                                <div className="font-semibold text-amber-900">ShipsGo API anahtarı eksik</div>
                                <div className="text-xs text-amber-800">Konteyner ekleyebilmek için Ayarlar → API Bağlantıları altından ShipsGo token'ınızı kaydedin.</div>
                            </div>
                            <button onClick={() => navigate('/settings?tab=api')}
                                className="h-8 px-3 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md whitespace-nowrap"
                                data-testid="logistics-goto-settings">
                                Ayarlara git
                            </button>
                        </div>
                    )}

                    <div className="bg-white border border-gray-200 rounded-lg mb-3 overflow-x-auto">
                        <div className="flex gap-1 p-1 min-w-max">
                            {[
                                { k: 'list', l: 'Konteynerlerim', icon: Anchor },
                                { k: 'add', l: 'Yeni Konteyner', icon: Plus },
                                { k: 'upload', l: 'BL Yükle (OCR)', icon: UploadCloud },
                                { k: 'bls', l: 'BL Kayıtları', icon: BookOpen },
                            ].map((t) => {
                                const Icon = t.icon;
                                const active = tab === t.k;
                                return (
                                    <button key={t.k} onClick={() => setTab(t.k)}
                                        className={`flex items-center gap-1.5 px-3 md:px-4 py-2 text-xs md:text-sm font-semibold rounded-md whitespace-nowrap transition-colors ${
                                            active ? 'bg-cyan-700 text-white' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                        data-testid={`logistics-tab-${t.k}`}>
                                        <Icon size={14} />{t.l}
                                    </button>
                                );
                            })}
                            <button onClick={() => navigate('/settings?tab=api')}
                                className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs md:text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md"
                                data-testid="logistics-settings-link">
                                <SettingsIcon size={14} /> Ayarlar
                            </button>
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-3 md:p-5">
                        {tab === 'list' && <ShipmentsList refreshKey={refreshKey} onBump={bump} />}
                        {tab === 'add' && <AddManual onAdded={() => { setTab('list'); bump(); }} />}
                        {tab === 'upload' && <UploadBL onAdded={() => { setTab('list'); bump(); }} onBlSaved={() => { setTab('bls'); bump(); }} />}
                        {tab === 'bls' && <BLsList refreshKey={refreshKey} onBump={bump} />}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

/* ─── LIST ─────────────────────────────────────────────────────────────────── */

function ShipmentsList({ refreshKey, onBump }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusCounts, setStatusCounts] = useState({});
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [refreshingAll, setRefreshingAll] = useState(false);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        axios.get(`${API}/api/logistics/shipments`, { headers: hdrs() })
            .then(r => {
                setItems(r.data?.items || []);
                setStatusCounts(r.data?.status_counts || {});
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load, refreshKey]);

    const refreshAll = async () => {
        setRefreshingAll(true);
        try {
            const r = await axios.post(`${API}/api/logistics/refresh-all`, {}, { headers: hdrs() });
            toast.success(`${r.data?.refreshed || 0} / ${r.data?.total || 0} konteyner güncellendi`);
            load();
        } catch (e) {
            toast.error(_err(e, 'Yenileme hatası'));
        } finally {
            setRefreshingAll(false);
        }
    };

    const [syncing, setSyncing] = useState(false);
    const syncFromShipsgo = async () => {
        setSyncing(true);
        try {
            const r = await axios.post(`${API}/api/logistics/sync`, {}, { headers: hdrs() });
            const { pulled, inserted, updated, skipped_deleted } = r.data || {};
            const parts = [`${pulled} çekildi`, `${inserted} yeni`, `${updated} güncellendi`];
            if (skipped_deleted) parts.push(`${skipped_deleted} silinmiş (atlandı)`);
            toast.success(`ShipsGo sync: ${parts.join(' · ')}`);
            load();
        } catch (e) {
            toast.error(_err(e, 'Sync hatası'));
        } finally {
            setSyncing(false);
        }
    };

    const filtered = items.filter(it => {
        if (filter !== 'all' && it.status !== filter) return false;
        if (search) {
            const s = search.toLowerCase();
            return (
                (it.container_number || '').toLowerCase().includes(s) ||
                (it.bl_number || '').toLowerCase().includes(s) ||
                (it.booking_number || '').toLowerCase().includes(s) ||
                (it.vessel_name || '').toLowerCase().includes(s) ||
                (it.reference || '').toLowerCase().includes(s)
            );
        }
        return true;
    });

    if (loading) return <div className="text-sm text-gray-500 py-6 text-center">Yükleniyor...</div>;

    return (
        <div className="space-y-3" data-testid="logistics-list">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[180px]">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Konteyner / BL / Vessel ara..."
                        className="w-full h-9 pl-8 pr-3 text-sm border border-gray-300 rounded-md"
                        data-testid="logistics-search" />
                </div>
                <select value={filter} onChange={(e) => setFilter(e.target.value)}
                    className="h-9 px-2 text-sm border border-gray-300 rounded-md"
                    data-testid="logistics-status-filter">
                    <option value="all">Tüm Durumlar ({items.length})</option>
                    {Object.entries(statusCounts).map(([k, v]) => (
                        <option key={k} value={k}>{STATUS_META[k]?.tr || k} ({v})</option>
                    ))}
                </select>
                <button onClick={refreshAll} disabled={refreshingAll || !items.length}
                    className="h-9 px-3 text-sm font-semibold text-white bg-cyan-700 hover:bg-cyan-800 disabled:opacity-40 rounded-md flex items-center gap-1.5"
                    data-testid="logistics-refresh-all">
                    {refreshingAll ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Tümünü Güncelle
                </button>
                <button onClick={syncFromShipsgo} disabled={syncing}
                    className="h-9 px-3 text-sm font-semibold text-cyan-800 bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 disabled:opacity-40 rounded-md flex items-center gap-1.5"
                    title="ShipsGo'daki tüm konteynerleri buraya çek"
                    data-testid="logistics-sync-shipsgo">
                    {syncing ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    ShipsGo'dan Çek
                </button>
            </div>

            {/* Status pills summary */}
            {Object.keys(statusCounts).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {Object.entries(statusCounts).map(([k, v]) => {
                        const meta = STATUS_META[k] || STATUS_META.NEW;
                        return (
                            <button key={k} onClick={() => setFilter(filter === k ? 'all' : k)}
                                className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full transition-all ${
                                    filter === k ? 'ring-2 ring-cyan-500 ring-offset-1' : ''
                                } ${meta.bg} ${meta.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                                {meta.tr} · {v}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* List */}
            {!filtered.length ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                    <Ship size={36} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">{items.length ? 'Filtreyle eşleşen konteyner yok' : 'Henüz konteyner eklenmedi'}</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((s) => (
                        <ShipmentCard key={s.id} s={s} open={openId === s.id}
                            onToggle={() => setOpenId(openId === s.id ? null : s.id)}
                            onChanged={() => { load(); onBump?.(); }} />
                    ))}
                </div>
            )}
        </div>
    );
}

function ShipmentCard({ s, open, onToggle, onChanged }) {
    const [busy, setBusy] = useState(false);
    const meta = STATUS_META[s.status] || STATUS_META.NEW;
    const progress = Math.max(0, Math.min(100, Number(s.transit_percentage || 0)));

    const doRefresh = async (e) => {
        e.stopPropagation();
        setBusy(true);
        try {
            await axios.post(`${API}/api/logistics/shipments/${s.id}/refresh`, {}, { headers: hdrs() });
            toast.success('Güncellendi');
            onChanged?.();
        } catch (err) {
            toast.error(_err(err, 'Yenileme hatası'));
        } finally {
            setBusy(false);
        }
    };

    const doDelete = async (e) => {
        e.stopPropagation();
        if (!window.confirm(`${s.container_number || s.bl_number} silinsin mi?\n\nNot: ShipsGo'dan da kalıcı olarak silinecek ve sync ettiğinde tekrar gelmeyecek.`)) return;
        setBusy(true);
        try {
            const r = await axios.delete(`${API}/api/logistics/shipments/${s.id}`, { headers: hdrs() });
            if (r.data?.shipsgo_error) {
                toast.warning(`Yerel silindi ✓ — ShipsGo: ${r.data.shipsgo_error}`);
            } else {
                toast.success(r.data?.shipsgo_deleted ? 'ShipsGo + Yerel silindi' : 'Silindi');
            }
            onChanged?.();
        } catch (err) {
            toast.error(_err(err, 'Silme hatası'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden hover:border-gray-300 transition-colors"
            data-testid={`shipment-card-${s.id}`}>
            <button onClick={onToggle} className="w-full text-left p-3 hover:bg-gray-50">
                <div className="flex items-start gap-3">
                    {open ? <ChevronDown size={16} className="text-gray-400 mt-0.5" /> : <ChevronRight size={16} className="text-gray-400 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-mono text-sm font-bold text-gray-900">
                                {s.container_number || s.bl_number || s.booking_number || '—'}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full ${meta.bg} ${meta.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                                {meta.tr}
                            </span>
                            {/* Multi-container badge (booking mode) */}
                            {(s.containers_list?.length || 0) > 1 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200 rounded-full"
                                    title={(s.containers_list || []).map(c => c.number).join(', ')}>
                                    📦 {s.containers_list.length} konteyner
                                </span>
                            )}
                            {s.carrier_name && (
                                <span className="text-[10px] text-gray-500 uppercase tracking-wider">{s.carrier_name}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 text-[11px] text-gray-600 flex-wrap">
                            {s.pol_name && (
                                <span className="flex items-center gap-1"><MapPin size={11} />{s.pol_name}</span>
                            )}
                            <span className="text-gray-300">→</span>
                            {s.pod_name && (
                                <span className="flex items-center gap-1 font-semibold text-gray-800"><MapPin size={11} />{s.pod_name}</span>
                            )}
                            {s.date_of_discharge && (
                                <span className="flex items-center gap-1 ml-auto">
                                    <Calendar size={11} /> ETA: <b>{fmt(s.date_of_discharge)}</b>
                                </span>
                            )}
                        </div>
                        {/* Progress bar */}
                        <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600 transition-all"
                                style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <button onClick={doRefresh} disabled={busy}
                            className="h-7 w-7 flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 rounded"
                            title="Yenile" data-testid={`refresh-${s.id}`}>
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                        </button>
                        <button onClick={doDelete} disabled={busy}
                            className="h-7 w-7 flex items-center justify-center text-gray-500 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30 rounded"
                            title="Sil" data-testid={`delete-${s.id}`}>
                            <Trash2 size={13} />
                        </button>
                    </div>
                </div>
            </button>

            {open && <ShipmentDetail s={s} />}
        </div>
    );
}

function ShipmentDetail({ s }) {
    const movements = (s.raw_shipsgo?.containers?.[0]?.movements || []).slice().reverse();
    const mapUrl = s.shipsgo_id && s.map_token
        ? `https://map.shipsgo.com/ocean/shipments/${s.shipsgo_id}?token=${s.map_token}`
        : null;
    return (
        <div className="border-t border-gray-200 bg-gray-50 p-3 md:p-4 space-y-3">
            {/* Quick info grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Info label="Vessel" v={s.vessel_name} />
                <Info label="Voyage" v={s.voyage} />
                <Info label="ETD" v={fmt(s.date_of_loading)} />
                <Info label="ETA" v={fmt(s.date_of_discharge)} />
                <Info label="Transit Süresi" v={s.transit_time ? `${s.transit_time} gün` : '—'} />
                <Info label="Konteyner Sayısı" v={s.container_count} />
                <Info label="BL No" v={s.bl_number} mono />
                <Info label="Booking" v={s.booking_number} mono />
            </div>

            {s.cargo_description && (
                <div className="text-xs">
                    <span className="text-gray-500">Yük: </span>
                    <span className="font-medium">{s.cargo_description}</span>
                </div>
            )}
            {s.shipper_name && (
                <div className="text-xs text-gray-600">
                    <span className="text-gray-400">Gönderici: </span><span className="font-medium">{s.shipper_name}</span>
                    {s.consignee_name && <> · <span className="text-gray-400">Alıcı: </span><span className="font-medium">{s.consignee_name}</span></>}
                </div>
            )}

            {/* Multi-container list (booking mode) */}
            {(s.containers_list?.length || 0) > 1 && (
                <div className="bg-white border border-gray-200 rounded-md p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">
                        Bu Gönderideki Konteynerler ({s.containers_list.length})
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                        {s.containers_list.map((c, i) => (
                            <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 border border-gray-100 rounded text-xs">
                                <span className="font-mono font-bold text-gray-800">{c.number}</span>
                                {c.size && <span className="text-[10px] text-gray-500">{c.size}'{c.type || ''}</span>}
                                {c.status && (
                                    <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 bg-cyan-100 text-cyan-700 rounded">
                                        {c.status}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Timeline */}
            {movements.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-md p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Olay Geçmişi</div>
                    <div className="space-y-1.5">
                        {movements.map((m, i) => {
                            const isAct = m.status === 'ACT';
                            return (
                                <div key={i} className="flex items-start gap-2 text-xs">
                                    <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${isAct ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-semibold">{EVENT_TR[m.event] || m.event}</span>
                                            <span className={`text-[9px] font-bold px-1.5 rounded ${isAct ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {isAct ? 'GERÇEKLEŞTİ' : 'TAHMİNİ'}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-gray-500">
                                            {m.location?.name} {m.vessel?.name && `· ⛵️ ${m.vessel.name}`}
                                        </div>
                                    </div>
                                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                        {m.timestamp?.slice(0, 16).replace('T', ' ')}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Products from OCR */}
            {(s.products || []).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-md p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Yük Detayı (BL'den)</div>
                    <div className="space-y-1">
                        {s.products.slice(0, 15).map((p, i) => (
                            <div key={i} className="flex items-center gap-2 text-[11px]">
                                <PkgIcon size={11} className="text-gray-400" />
                                <span className="flex-1 truncate">{p.description}</span>
                                <span className="font-mono text-gray-500">{p.quantity} {p.unit || ''}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex items-center gap-2">
                {mapUrl && (
                    <a href={mapUrl} target="_blank" rel="noreferrer"
                        className="h-8 px-3 text-xs font-semibold text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-md flex items-center gap-1.5"
                        data-testid={`map-link-${s.id}`}>
                        <ExternalLink size={11} /> ShipsGo Haritada Aç
                    </a>
                )}
                {s.co2_emission && (
                    <span className="text-[10px] text-gray-500 ml-auto">
                        CO₂: {Number(s.co2_emission).toFixed(2)} ton
                    </span>
                )}
            </div>
        </div>
    );
}

function Info({ label, v, mono }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</div>
            <div className={`text-xs font-semibold text-gray-800 ${mono ? 'font-mono' : ''}`}>{v || '—'}</div>
        </div>
    );
}

/* ─── ADD MANUAL ──────────────────────────────────────────────────────────── */

function AddManual({ onAdded }) {
    const [mode, setMode] = useState('container');
    const [value, setValue] = useState('');
    const [scac, setScac] = useState('');
    const [reference, setReference] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        const v = value.trim().toUpperCase();
        if (!v) { toast.error('Numara gerekli'); return; }
        setLoading(true);
        try {
            const body = { reference: reference || null };
            if (mode === 'container') body.container_number = v;
            if (mode === 'booking') body.booking_number = v;
            if (mode === 'bl') body.bl_number = v;
            if (scac) body.carrier_scac = scac.trim().toUpperCase();
            const r = await axios.post(`${API}/api/logistics/shipments`, body, { headers: hdrs() });
            if (r.data?.duplicate) {
                toast.info('Bu numara zaten kayıtlı');
            } else {
                toast.success('Konteyner eklendi, ShipsGo verileri çekiliyor...');
            }
            setValue(''); setScac(''); setReference('');
            onAdded?.();
        } catch (e) {
            toast.error(_err(e, 'Ekleme hatası'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4 max-w-2xl" data-testid="logistics-manual-form">
            <div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Numara Tipi</div>
                <div className="flex gap-2 flex-wrap">
                    {[
                        { k: 'container', l: 'Konteyner No', ex: 'MSCU1234567' },
                        { k: 'bl', l: 'BL / MBL', ex: 'MEDUH1234567' },
                        { k: 'booking', l: 'Booking', ex: 'BOOKING12345' },
                    ].map(o => (
                        <button key={o.k} onClick={() => setMode(o.k)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-md ${
                                mode === o.k ? 'bg-cyan-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            data-testid={`mode-${o.k}`}>
                            {o.l}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <label className="block">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-600">
                        {mode === 'container' ? 'Konteyner Numarası' : mode === 'bl' ? 'BL / MBL Numarası' : 'Booking Numarası'}
                    </span>
                    <input value={value} onChange={(e) => setValue(e.target.value.toUpperCase())}
                        placeholder={mode === 'container' ? 'Ör: MSCU1234567' : mode === 'bl' ? 'Ör: MEDUH1234567' : 'Ör: BOOKING12345'}
                        className="mt-1 w-full h-10 px-3 text-base font-mono border border-gray-300 rounded-md focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                        data-testid="logistics-number-input" />
                </label>
                <p className="text-[11px] text-gray-500 mt-1">
                    {mode === 'container' && '4 büyük harf + 7 rakam formatı (ISO 6346).'}
                    {mode === 'bl' && 'Master Bill of Lading numarası. Taşıyıcıyı tahmin edemezsek SCAC kodunu da gir.'}
                    {mode === 'booking' && 'Taşıma şirketinin verdiği rezervasyon numarası.'}
                </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <label className="block">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Taşıyıcı SCAC (opsiyonel)</span>
                    <input value={scac} onChange={(e) => setScac(e.target.value.toUpperCase())}
                        placeholder="MAEU, MSCU, OOLU..."
                        className="mt-1 w-full h-9 px-3 text-sm font-mono border border-gray-300 rounded-md"
                        data-testid="logistics-scac-input" />
                </label>
                <label className="block">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Referans (opsiyonel)</span>
                    <input value={reference} onChange={(e) => setReference(e.target.value)}
                        placeholder="Sipariş #, PO No vb."
                        className="mt-1 w-full h-9 px-3 text-sm border border-gray-300 rounded-md"
                        data-testid="logistics-reference-input" />
                </label>
            </div>

            <button onClick={submit} disabled={loading || !value}
                className="h-10 px-5 text-sm font-semibold text-white bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50 rounded-md flex items-center gap-2"
                data-testid="logistics-add-submit">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {loading ? 'Ekleniyor...' : 'Sisteme Ekle ve Takibe Başla'}
            </button>
        </div>
    );
}

/* ─── UPLOAD BL (OCR) ─────────────────────────────────────────────────────── */

function UploadBL({ onAdded, onBlSaved }) {
    const [file, setFile] = useState(null);
    const [extracting, setExtracting] = useState(false);
    const [adding, setAdding] = useState(false);
    const [savingBl, setSavingBl] = useState(false);
    const [data, setData] = useState(null);
    const [drag, setDrag] = useState(false);
    const [saveMode, setSaveMode] = useState('booking'); // 'booking' = 1 credit, 'each' = N credits
    const [dupInfo, setDupInfo] = useState(null);  // {duplicates:{}, dup_count, new_count, total}

    // OCR sonrası DB'de var mı kontrol et
    useEffect(() => {
        if (!data) { setDupInfo(null); return; }
        const containers = data.container_numbers || [];
        const booking = data.booking_number;
        const bl = data.bl_number;
        if (!containers.length && !booking && !bl) { setDupInfo(null); return; }
        axios.post(`${API}/api/logistics/check-duplicates`, {
            container_numbers: containers, booking_number: booking, bl_number: bl,
        }, { headers: hdrs() })
            .then(r => setDupInfo(r.data))
            .catch(() => setDupInfo(null));
    }, [data]);

    const saveBlOnly = async () => {
        if (!data) return;
        if (!data.bl_number && !data.booking_number) {
            toast.error('BL veya Booking numarası gerekli');
            return;
        }
        setSavingBl(true);
        try {
            const r = await axios.post(`${API}/api/logistics/bls`, {
                ...data,
                file_name: file?.name || '',
            }, { headers: hdrs() });
            toast.success(r.data?.duplicate ? 'BL güncellendi' : 'BL kaydedildi');
            setFile(null); setData(null);
            onBlSaved?.();
        } catch (e) {
            toast.error(_err(e, 'BL kaydedilemedi'));
        } finally {
            setSavingBl(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault(); setDrag(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) setFile(f);
    };

    const extract = async () => {
        if (!file) return;
        setExtracting(true); setData(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            // 1) Upload + start async job (returns immediately, no Cloudflare 100s timeout)
            const startResp = await axios.post(`${API}/api/logistics/bl/extract-async`, fd, {
                headers: { ...hdrs(), 'Content-Type': 'multipart/form-data' },
                timeout: 60000,
            });
            const jobId = startResp.data?.job_id;
            if (!jobId) throw new Error('Job ID alınamadı');

            // 2) Poll status (every 2s, max 5min)
            const startTs = Date.now();
            const maxWaitMs = 5 * 60 * 1000;
            let result = null;
            while (true) {
                if (Date.now() - startTs > maxWaitMs) {
                    throw new Error('OCR çok uzun sürdü (5dk timeout)');
                }
                await new Promise((res) => setTimeout(res, 2000));
                let pollResp;
                try {
                    pollResp = await axios.get(`${API}/api/logistics/bl/extract-job/${jobId}`, {
                        headers: hdrs(), timeout: 15000,
                    });
                } catch (pe) {
                    // 404 nadiren olur (TTL); kullanıcıya net hata göster
                    if (pe.response?.status === 404) throw new Error('OCR job bulunamadı');
                    // Network/proxy glitch — devam et
                    continue;
                }
                const st = pollResp.data?.status;
                if (st === 'done') { result = pollResp.data; break; }
                if (st === 'error') { throw new Error(pollResp.data?.error || 'OCR hatası'); }
                // queued/processing — devam
            }

            const d = result?.data || {};
            setData(d);
            const found = d.container_numbers?.length || 0;
            if (!found && !d.bl_number) {
                toast.warning('Numara çıkarılamadı, manuel girmeniz gerekebilir');
            } else {
                toast.success(`${found} konteyner + ${d.bl_number ? 'BL' : ''} algılandı`);
            }
        } catch (e) {
            toast.error(_err(e, 'OCR hatası'));
        } finally {
            setExtracting(false);
        }
    };

    const addAll = async () => {
        if (!data) return;
        const containers = data.container_numbers || [];
        const hasBooking = Boolean(data.booking_number || data.bl_number);
        if (!containers.length && !data.bl_number && !data.booking_number) {
            toast.error('Eklenecek numara yok');
            return;
        }
        setAdding(true);
        let ok = 0, fail = 0, dup = 0;
        let lastErr = null;
        let blSaved = false;

        // 0) Önce BL kaydını "BL Kayıtları" sekmesine ekle (idempotent — duplicate ise update)
        if (data.bl_number || data.booking_number) {
            try {
                await axios.post(`${API}/api/logistics/bls`, {
                    ...data,
                    file_name: file?.name || '',
                }, { headers: hdrs() });
                blSaved = true;
            } catch (e) {
                console.error('BL save failed', e?.response?.data || e);
            }
        }

        const sharedFields = {
            bl_number: data.bl_number || null,
            carrier_scac: data.carrier_scac || null,
            reference: data.bl_number || data.booking_number || null,
            shipper_name: data.shipper_name,
            consignee_name: data.consignee_name,
            cargo_description: data.cargo_description,
            incoterms: data.incoterms,
            total_weight_kg: data.total_weight_kg,
            total_packages: data.total_packages,
            products: data.products || [],
            expected_containers: containers,  // metadata for display
        };

        const useBookingMode = saveMode === 'booking' && hasBooking;

        if (useBookingMode) {
            // Single shipment via booking/BL — ShipsGo groups all sibling containers under it (1 credit)
            try {
                const r = await axios.post(`${API}/api/logistics/shipments`, {
                    ...sharedFields,
                    booking_number: data.booking_number || null,
                }, { headers: hdrs() });
                if (r.data?.duplicate) dup++; else ok++;
            } catch (e) { fail++; lastErr = e; console.error('shipments POST failed', e?.response?.data || e); }
        } else if (containers.length === 0) {
            // Fallback: BL only, no containers detected
            try {
                const r = await axios.post(`${API}/api/logistics/shipments`, {
                    ...sharedFields,
                    booking_number: data.booking_number || null,
                }, { headers: hdrs() });
                if (r.data?.duplicate) dup++; else ok++;
            } catch (e) { fail++; lastErr = e; console.error('shipments POST failed', e?.response?.data || e); }
        } else {
            // Per-container mode (N credits — explicit user choice)
            for (const cn of containers) {
                try {
                    const r = await axios.post(`${API}/api/logistics/shipments`, {
                        container_number: cn,
                        ...sharedFields,
                    }, { headers: hdrs() });
                    if (r.data?.duplicate) dup++; else ok++;
                } catch (e) { fail++; lastErr = e; console.error('shipments POST failed', e?.response?.data || e); }
            }
        }

        setAdding(false);
        if (ok) {
            const msg = useBookingMode
                ? `Booking eklendi (1 kredi) — ShipsGo ${containers.length} konteyneri tek shipment'ta gruplayacak${blSaved ? ' · BL kaydedildi' : ''}`
                : `${ok} yeni konteyner eklendi${dup ? `, ${dup} zaten vardı` : ''}${blSaved ? ' · BL kaydedildi' : ''}`;
            toast.success(msg);
        } else if (blSaved && (containers.length === 0 || dup)) {
            toast.success('BL kaydedildi');
        }
        if (fail) {
            const detail = _err(lastErr, '');
            toast.error(detail ? `${fail} eklenemedi: ${detail}` : `${fail} eklenemedi`);
        }
        if (ok || dup || blSaved) {
            setFile(null); setData(null);
            onAdded?.();
            onBlSaved?.();
        }
    };

    return (
        <div className="space-y-4" data-testid="logistics-upload">
            {/* Drop zone */}
            {!file && (
                <label
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={onDrop}
                    className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        drag ? 'border-cyan-500 bg-cyan-50' : 'border-gray-300 hover:border-cyan-400 hover:bg-gray-50'
                    }`}
                    data-testid="logistics-dropzone">
                    <UploadCloud size={36} className="mx-auto text-gray-400 mb-2" />
                    <div className="text-sm font-semibold text-gray-700">BL Dosyasını Sürükle veya Tıkla</div>
                    <div className="text-xs text-gray-500 mt-1">PDF · PNG · JPG · WebP (max 20 MB)</div>
                    <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp"
                        onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
                        data-testid="logistics-file-input" />
                </label>
            )}

            {file && !data && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-3">
                    <FileText size={20} className="text-cyan-600" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{file.name}</div>
                        <div className="text-[11px] text-gray-500">{(file.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <button onClick={() => setFile(null)} className="h-7 w-7 flex items-center justify-center text-gray-500 hover:bg-gray-200 rounded">
                        <X size={14} />
                    </button>
                    <button onClick={extract} disabled={extracting}
                        className="h-9 px-4 text-sm font-semibold text-white bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                        data-testid="logistics-extract-btn">
                        {extracting ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                        {extracting ? 'OCR çalışıyor...' : 'OCR ile Çıkar'}
                    </button>
                </div>
            )}

            {data && (
                <div className="border border-cyan-200 bg-cyan-50/40 rounded-lg p-4 space-y-3" data-testid="logistics-ocr-result">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-cyan-700 text-white rounded-full flex items-center justify-center text-xs font-bold">✓</div>
                        <div className="text-sm font-bold">OCR Sonuçları (Gemini)</div>
                        <button onClick={() => { setData(null); setFile(null); }}
                            className="ml-auto text-xs text-gray-500 hover:text-gray-700">Sıfırla</button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                        <Info label="BL Numarası" v={data.bl_number} mono />
                        <Info label="Booking" v={data.booking_number} mono />
                        <Info label="Carrier" v={data.carrier_name} />
                        <Info label="SCAC" v={data.carrier_scac} mono />
                        <Info label="Vessel" v={data.vessel_name} />
                        <Info label="Voyage" v={data.voyage_number} mono />
                        <Info label="POL" v={data.port_of_loading} />
                        <Info label="POD" v={data.port_of_discharge} />
                        <Info label="ETD" v={data.etd} />
                        <Info label="ETA" v={data.eta} />
                        <Info label="Incoterms" v={data.incoterms} />
                        <Info label="Ağırlık (kg)" v={data.total_weight_kg} />
                    </div>

                    {/* Duplicate warning — BEFORE user clicks "Ekle" */}
                    {dupInfo && dupInfo.dup_count > 0 && (
                        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3" data-testid="dup-warning">
                            <div className="flex items-start gap-2">
                                <AlertCircle size={16} className="text-amber-700 shrink-0 mt-0.5" />
                                <div className="flex-1 text-xs">
                                    <div className="font-bold text-amber-900 mb-1">
                                        ⚠️ {dupInfo.dup_count} / {dupInfo.total} numara zaten sistemde kayıtlı
                                    </div>
                                    <div className="text-amber-800 mb-2">
                                        Kredi harcamamak için bu numaralar atlanacak. Sadece <b>{dupInfo.new_count} yeni</b> kayıt için ShipsGo'ya gidilecek.
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {Object.keys(dupInfo.duplicates).map(k => (
                                            <span key={k} className="px-1.5 py-0.5 text-[10px] font-mono font-semibold bg-white border border-amber-300 text-amber-900 rounded">
                                                {k}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">
                            Tespit Edilen Konteynerler ({data.container_numbers?.length || 0})
                        </div>
                        {data.container_numbers?.length ? (
                            <div className="flex flex-wrap gap-1.5">
                                {data.container_numbers.map((cn, i) => (
                                    <span key={i} className="px-2 py-1 text-xs font-mono font-semibold bg-white border border-cyan-300 text-cyan-900 rounded">
                                        {cn}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div className="text-xs text-gray-500 italic">Konteyner numarası bulunamadı — sadece BL ile eklenecek</div>
                        )}
                    </div>

                    {data.cargo_description && (
                        <div className="text-xs">
                            <span className="text-gray-500 font-bold uppercase tracking-wider">Yük: </span>
                            {data.cargo_description}
                        </div>
                    )}

                    {(data.shipper_name || data.consignee_name) && (
                        <div className="grid grid-cols-2 gap-3 text-xs">
                            <Info label="Gönderici" v={data.shipper_name} />
                            <Info label="Alıcı" v={data.consignee_name} />
                        </div>
                    )}

                    {(data.products || []).length > 0 && (
                        <details className="text-xs">
                            <summary className="cursor-pointer text-gray-700 font-semibold">
                                Ürünler ({data.products.length})
                            </summary>
                            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                                {data.products.map((p, i) => (
                                    <div key={i} className="flex gap-2 px-2 py-1 bg-white rounded">
                                        <PkgIcon size={11} className="text-gray-400 mt-0.5" />
                                        <span className="flex-1">{p.description}</span>
                                        <span className="font-mono">{p.quantity} {p.unit || ''}</span>
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}

                    <div className="flex items-center gap-2 pt-2 border-t border-cyan-200 flex-wrap">
                        {/* Kredi modu seçimi — sadece booking/BL varsa */}
                        {(data.booking_number || data.bl_number) && (data.container_numbers?.length || 0) > 1 && (
                            <div className="w-full mb-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-900 mb-1.5">
                                    💰 ShipsGo Kredi Kullanımı
                                </div>
                                <div className="space-y-1.5">
                                    <label className="flex items-start gap-2 text-xs cursor-pointer">
                                        <input type="radio" name="saveMode" value="booking"
                                            checked={saveMode === 'booking'} onChange={() => setSaveMode('booking')}
                                            className="mt-0.5"
                                            data-testid="save-mode-booking" />
                                        <div>
                                            <div className="font-bold text-emerald-700">
                                                ✓ Tek shipment (1 kredi)  ·  Tavsiye edilen
                                            </div>
                                            <div className="text-[11px] text-gray-700">
                                                Booking <code className="bg-white px-1 rounded">{data.booking_number || data.bl_number}</code> ile tek sorgu — ShipsGo {data.container_numbers.length} konteyneri otomatik gruplar, hepsinin pozisyonu/olayları aynı shipment altında görünür.
                                            </div>
                                        </div>
                                    </label>
                                    <label className="flex items-start gap-2 text-xs cursor-pointer">
                                        <input type="radio" name="saveMode" value="each"
                                            checked={saveMode === 'each'} onChange={() => setSaveMode('each')}
                                            className="mt-0.5"
                                            data-testid="save-mode-each" />
                                        <div>
                                            <div className="font-bold text-rose-700">
                                                Her konteyner ayrı ({data.container_numbers.length} kredi)
                                            </div>
                                            <div className="text-[11px] text-gray-600">
                                                Her konteyner ayrı shipment olarak kaydedilir. Sadece konteynerler farklı gemilerde/rotalarda ise gerekli.
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        )}

                        <button onClick={addAll} disabled={adding}
                            className="h-10 px-5 text-sm font-semibold text-white bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50 rounded-md flex items-center gap-2"
                            data-testid="logistics-add-all-btn">
                            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            {adding
                                ? 'Ekleniyor...'
                                : saveMode === 'booking' && (data.booking_number || data.bl_number) && (data.container_numbers?.length || 0) > 1
                                    ? `Tek Shipment Olarak Ekle (1 kredi · ${data.container_numbers.length} konteyner)`
                                    : `${data.container_numbers?.length || 1} Yükü Sisteme Ekle ve Takibe Başla`}
                        </button>
                        <button onClick={saveBlOnly} disabled={savingBl || (!data.bl_number && !data.booking_number)}
                            className="h-10 px-4 text-sm font-semibold text-cyan-800 bg-white border-2 border-cyan-300 hover:bg-cyan-50 disabled:opacity-40 rounded-md flex items-center gap-2"
                            title="Sadece BL bilgilerini kaydet (ShipsGo'ya gönderme, kredi harcamaz)"
                            data-testid="logistics-save-bl-btn">
                            {savingBl ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            {savingBl ? 'Kaydediliyor...' : 'Sadece BL Kaydet (kredi yok)'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── BL KAYITLARI ─────────────────────────────────────────────────────────── */

function BLsList({ refreshKey, onBump }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [openId, setOpenId] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        axios.get(`${API}/api/logistics/bls`, { headers: hdrs(), params: { search, limit: 500 } })
            .then(r => setItems(r.data?.items || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [search]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const del = async (id) => {
        if (!window.confirm('BL kaydı silinsin mi?')) return;
        try {
            await axios.delete(`${API}/api/logistics/bls/${id}`, { headers: hdrs() });
            toast.success('Silindi'); load(); onBump?.();
        } catch (e) { toast.error(_err(e, 'Silme hatası')); }
    };

    if (loading) return <div className="text-sm text-gray-500 py-6 text-center">Yükleniyor...</div>;

    return (
        <div className="space-y-3" data-testid="bls-list">
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="BL / Booking / Carrier / Shipper ara..."
                        className="w-full h-9 pl-8 pr-3 text-sm border border-gray-300 rounded-md"
                        data-testid="bls-search" />
                </div>
                <button onClick={load} className="h-9 px-3 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center gap-1.5">
                    <RefreshCw size={14} /> Yenile
                </button>
            </div>

            {!items.length ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                    <BookOpen size={36} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">Henüz BL kaydı yok</p>
                    <p className="text-xs text-gray-400 mt-1">BL Yükle (OCR) sekmesinden ekleyebilirsin</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((bl) => (
                        <BLCard key={bl.id} bl={bl} open={openId === bl.id}
                            onToggle={() => setOpenId(openId === bl.id ? null : bl.id)}
                            onDelete={() => del(bl.id)} />
                    ))}
                </div>
            )}
        </div>
    );
}

function BLCard({ bl, open, onToggle, onDelete }) {
    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden hover:border-gray-300 transition-colors"
            data-testid={`bl-card-${bl.id}`}>
            <button onClick={onToggle} className="w-full text-left p-3 hover:bg-gray-50">
                <div className="flex items-start gap-3">
                    {open ? <ChevronDown size={16} className="text-gray-400 mt-0.5" /> : <ChevronRight size={16} className="text-gray-400 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <FileText size={14} className="text-cyan-700" />
                            <span className="font-mono font-bold text-gray-900">{bl.bl_number || bl.booking_number || '—'}</span>
                            {bl.bl_number && bl.booking_number && (
                                <span className="text-[10px] text-gray-500">Booking: <span className="font-mono">{bl.booking_number}</span></span>
                            )}
                            {bl.carrier_name && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{bl.carrier_name}</span>
                            )}
                            {(bl.container_numbers?.length > 0) && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-cyan-50 text-cyan-700 rounded">
                                    {bl.container_numbers.length} konteyner
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 text-[11px] text-gray-600 flex-wrap">
                            {bl.vessel_name && <span className="flex items-center gap-1"><Ship size={11} />{bl.vessel_name}</span>}
                            {bl.port_of_loading && <span className="flex items-center gap-1"><MapPin size={11} />{bl.port_of_loading}</span>}
                            {bl.port_of_discharge && <span>→ {bl.port_of_discharge}</span>}
                            {bl.eta && <span className="flex items-center gap-1"><Calendar size={11} />ETA {fmt(bl.eta)}</span>}
                        </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="h-7 w-7 flex items-center justify-center rounded text-rose-600 hover:bg-rose-50"
                        data-testid={`bl-delete-${bl.id}`}>
                        <Trash2 size={13} />
                    </button>
                </div>
            </button>

            {open && (
                <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50/40">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                        <Info label="BL No" v={bl.bl_number} mono />
                        <Info label="Booking" v={bl.booking_number} mono />
                        <Info label="Carrier" v={bl.carrier_name} />
                        <Info label="SCAC" v={bl.carrier_scac} mono />
                        <Info label="Vessel" v={bl.vessel_name} />
                        <Info label="Voyage" v={bl.voyage_number} mono />
                        <Info label="POL" v={bl.port_of_loading} />
                        <Info label="POD" v={bl.port_of_discharge} />
                        <Info label="Place of Receipt" v={bl.place_of_receipt} />
                        <Info label="Place of Delivery" v={bl.place_of_delivery} />
                        <Info label="ETD" v={fmt(bl.etd)} />
                        <Info label="ETA" v={fmt(bl.eta)} />
                        <Info label="Incoterms" v={bl.incoterms} />
                        <Info label="Ağırlık (kg)" v={bl.total_weight_kg} />
                        <Info label="Hacim (m³)" v={bl.total_volume_m3} />
                        <Info label="Paket" v={bl.total_packages} />
                        <Info label="Shipper" v={bl.shipper_name} />
                        <Info label="Consignee" v={bl.consignee_name} />
                    </div>

                    {bl.cargo_description && (
                        <div className="text-xs">
                            <span className="text-gray-500 font-bold uppercase tracking-wider">Yük tanımı: </span>
                            {bl.cargo_description}
                        </div>
                    )}

                    {(bl.container_numbers?.length > 0) && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">
                                Konteynerler ({bl.container_numbers.length})
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {bl.container_numbers.map((cn, i) => (
                                    <span key={i} className="px-2 py-1 text-xs font-mono font-semibold bg-white border border-cyan-300 text-cyan-900 rounded">
                                        {cn}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {(bl.products || []).length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">
                                Ürünler ({bl.products.length})
                            </div>
                            <div className="space-y-1 max-h-48 overflow-y-auto bg-white rounded border border-gray-200 p-2">
                                {bl.products.map((p, i) => (
                                    <div key={i} className="flex items-center gap-2 text-xs py-1">
                                        <PkgIcon size={11} className="text-gray-400" />
                                        <span className="flex-1">{p.description}</span>
                                        {p.hs_code && <span className="text-[10px] text-gray-500 font-mono">HS:{p.hs_code}</span>}
                                        <span className="font-mono font-semibold">{p.quantity} {p.unit || ''}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between text-[10px] text-gray-400 pt-2 border-t border-gray-200">
                        <span>Kaydedildi: {fmt(bl.created_at)}{bl.file_name ? ` · ${bl.file_name}` : ''}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

/* Utils */
function _err(e, fallback) {
    const d = e?.response?.data?.detail;
    if (!d) return fallback;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(', ');
    return JSON.stringify(d).slice(0, 200);
}
